import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Info, Plus, X } from "lucide-react";

const VEHICLE_TYPES = [
  { value: "2_WHEELER", label: "2-Wheeler (Bike/Scooter)", capacity: 30 },
  { value: "3_WHEELER", label: "3-Wheeler (Auto/Tempo)", capacity: 200 },
  { value: "4_WHEELER", label: "4-Wheeler (Car/Van)", capacity: 500 },
  { value: "MINI_TRUCK", label: "Mini Truck (up to 1 ton)", capacity: 1000 },
  { value: "TEMPO_407", label: "Tempo / 407 (1–2.5 ton)", capacity: 2500 },
  { value: "TRUCK", label: "Truck (2.5 ton+)", capacity: 5000 },
];

export const emptyVehicle = () => ({
  id: Math.random().toString(36).slice(2),
  vehicle_number: "",
  vehicle_type: "",
  capacity_kg: "",
  ownership: "OWN",
});

export const validateStep5 = () => ({}); // entirely optional

export default function Step5Fleet({ formData, setFormData }) {
  const vehicles = formData.vehicles || [];

  const updateVehicle = (idx, patch) => {
    const next = vehicles.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    setFormData((p) => ({ ...p, vehicles: next }));
  };

  const addVehicle = () =>
    setFormData((p) => ({ ...p, vehicles: [...(p.vehicles || []), emptyVehicle()] }));

  const removeVehicle = (idx) =>
    setFormData((p) => ({
      ...p,
      vehicles: (p.vehicles || []).filter((_, i) => i !== idx),
    }));

  const onTypeChange = (idx, type) => {
    const meta = VEHICLE_TYPES.find((t) => t.value === type);
    const current = vehicles[idx] || {};
    // Only auto-suggest if capacity is empty or matched a previous suggestion
    const previousSuggestion = VEHICLE_TYPES.find(
      (t) => t.value === current.vehicle_type
    )?.capacity;
    const shouldAutofill =
      !current.capacity_kg ||
      current.capacity_kg === "" ||
      Number(current.capacity_kg) === previousSuggestion;
    updateVehicle(idx, {
      vehicle_type: type,
      capacity_kg: shouldAutofill && meta ? meta.capacity : current.capacity_kg,
    });
  };

  const validVehicles = vehicles.filter(
    (v) => v.vehicle_number?.trim() || v.vehicle_type
  );
  const totalCapacity = validVehicles.reduce(
    (s, v) => s + Number(v.capacity_kg || 0),
    0
  );
  const ownCount = validVehicles.filter((v) => v.ownership === "OWN").length;
  const hiredCount = validVehicles.filter((v) => v.ownership === "HIRED").length;

  return (
    <div className="space-y-6" data-testid="step5-fleet">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Vehicle Fleet</h2>
        <p className="text-sm text-slate-500">
          List vehicles owned or hired by this partner (optional)
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-800">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Vehicle details help with route assignment. You can also add vehicles later
          from the partner profile page.
        </span>
      </div>

      {/* Vehicle rows */}
      <div className="space-y-3">
        {(vehicles.length ? vehicles : [emptyVehicle()]).map((v, idx) => (
          <div
            key={v.id || idx}
            className="border border-slate-200 rounded-lg p-3 bg-white relative"
            data-testid={`vehicle-row-${idx}`}
          >
            {vehicles.length > 1 && (
              <button
                type="button"
                onClick={() => removeVehicle(idx)}
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 transition"
                aria-label="Remove vehicle"
                data-testid={`vehicle-remove-${idx}`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label>Vehicle Number</Label>
                <Input
                  value={v.vehicle_number || ""}
                  onChange={(e) =>
                    updateVehicle(idx, {
                      vehicle_number: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="AP31AB1234"
                  data-testid={`vehicle-number-${idx}`}
                />
              </div>
              <div>
                <Label>Vehicle Type</Label>
                <select
                  value={v.vehicle_type || ""}
                  onChange={(e) => onTypeChange(idx, e.target.value)}
                  className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                  data-testid={`vehicle-type-${idx}`}
                >
                  <option value="">Select…</option>
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Capacity (kg)</Label>
                <Input
                  type="number"
                  min="0"
                  value={v.capacity_kg ?? ""}
                  onChange={(e) =>
                    updateVehicle(idx, {
                      capacity_kg: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  placeholder="500"
                  data-testid={`vehicle-capacity-${idx}`}
                />
              </div>
              <div>
                <Label>Ownership</Label>
                <div className="flex">
                  {["OWN", "HIRED"].map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => updateVehicle(idx, { ownership: o })}
                      className={`flex-1 px-3 h-10 border text-xs font-semibold transition first:rounded-l last:rounded-r ${
                        (v.ownership || "OWN") === o
                          ? "bg-slate-800 text-white border-slate-800"
                          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                      }`}
                      data-testid={`vehicle-ownership-${idx}-${o.toLowerCase()}`}
                    >
                      {o === "OWN" ? "Own" : "Hired"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addVehicle}
        className="border-dashed"
        data-testid="add-vehicle-btn"
      >
        <Plus className="w-4 h-4 mr-1" /> Add Vehicle
      </Button>

      {/* Summary */}
      <div
        className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1"
        data-testid="fleet-summary"
      >
        <span>
          <strong className="text-slate-800">Total:</strong> {validVehicles.length}{" "}
          vehicles
        </span>
        <span>
          <strong className="text-slate-800">Capacity:</strong> {totalCapacity}kg
        </span>
        <span>
          <strong className="text-slate-800">Own:</strong> {ownCount}
        </span>
        <span>
          <strong className="text-slate-800">Hired:</strong> {hiredCount}
        </span>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
      {children}
    </label>
  );
}
